// Google Maps integration for Savings Summary Transaction Locations
export default class TransactionMap {
  constructor() {
    this.map = null;
    this.markers = [];
    this.infoWindow = null;
    this.geocoder = null;
    this.savingsSummaryId = null;
    this.currentStartDate = null;
    this.currentEndDate = null;

    this.init();
  }

  init() {
    // Get savings summary ID from URL
    const pathParts = window.location.pathname.split('/');
    this.savingsSummaryId = pathParts[pathParts.length - 1];

    // Initialize map when Google Maps API is loaded
    if (typeof google !== 'undefined' && google.maps) {
      this.initializeMap();
    } else {
      this.loadGoogleMapsAPI();
    }
  }

  loadGoogleMapsAPI() {
    // Google Maps API is already loaded in the layout, so just initialize
    this.initializeMap();
  }

  initializeMap() {
    const mapElement = document.getElementById('google-map');
    const loadingElement = document.getElementById('map-loading');

    if (!mapElement) return;

    // Initialize map centered on US
    this.map = new google.maps.Map(mapElement, {
      zoom: 3,
      center: { lat: 39.8283, lng: -98.5795 }, // Center of US
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ]
    });

    this.geocoder = new google.maps.Geocoder();
    this.infoWindow = new google.maps.InfoWindow();

    // Show map and hide loading
    mapElement.style.display = 'block';
    loadingElement.style.display = 'none';

    // Load initial transaction data
    this.loadTransactionLocations();
  }

  async loadTransactionLocations() {
    if (!this.savingsSummaryId) return;

    try {
      const params = new URLSearchParams();
      if (this.currentStartDate) params.append('start_date', this.currentStartDate);
      if (this.currentEndDate) params.append('end_date', this.currentEndDate);

      const response = await fetch(`/savings_summaries/${this.savingsSummaryId}/transaction_locations?${params}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const locationData = await response.json();

      this.clearMarkers();
      this.addMarkersForLocations(locationData);
    } catch (error) {
      console.error('Error loading transaction locations:', error);
      this.showErrorMessage('Failed to load transaction locations. Please try again.');
    }
  }

  showErrorMessage(message) {
    const mapElement = document.getElementById('google-map');
    const loadingElement = document.getElementById('map-loading');

    loadingElement.innerHTML = `
      <div class="text-center">
        <svg class="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="mt-2 text-sm font-medium text-gray-900">Error Loading Map</p>
        <p class="text-sm text-gray-500">${message}</p>
        <button onclick="window.transactionMap.loadTransactionLocations()" 
                class="mt-2 text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
          Retry
        </button>
      </div>
    `;

    mapElement.style.display = 'none';
    loadingElement.style.display = 'flex';
  }

  clearMarkers() {
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
  }

  async addMarkersForLocations(locationData) {
    if (!locationData || locationData.length === 0) {
      this.showNoDataMessage();
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    let markersAdded = 0;

    for (const location of locationData) {
      try {
        const coordinates = {lat: parseFloat(location.latitude), lng: parseFloat(location.longitude)}
        if (coordinates) {
          const marker = this.createMarker(location, coordinates);
          this.markers.push(marker);
          bounds.extend(coordinates);
          markersAdded++;
        }
      } catch (error) {
        console.warn(`Failed to geocode ${location.city}, ${location.state}:`, error);
      }
    }

    // Fit map to show all markers
    if (markersAdded > 0) {
      this.map.fitBounds(bounds);

      // Don't zoom in too much for single markers
      google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 12) {
          this.map.setZoom(12);
        }
      });
    } else {
      this.showNoDataMessage();
    }
  }

  showNoDataMessage() {
    const mapElement = document.getElementById('google-map');
    const loadingElement = document.getElementById('map-loading');

    loadingElement.innerHTML = `
      <div class="text-center">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <p class="mt-2 text-sm font-medium text-gray-900">No Transaction Locations</p>
        <p class="text-sm text-gray-500">No transactions found for the selected date range</p>
      </div>
    `;

    mapElement.style.display = 'none';
    loadingElement.style.display = 'flex';
  }

  createMarker(location, coordinates) {
    // Determine marker color and size based on volume
    const { color, scale } = this.getMarkerStyle(location.total_gallons);

    const marker = new google.maps.Marker({
      position: coordinates,
      map: this.map,
      title: `${location.city}, ${location.state}`,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: color,
        fillOpacity: 0.8,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: scale
      }
    });

    // // Add click listener for info window
    // marker.addListener('click', () => {
    //   this.showInfoWindow(marker, location);
    // });

    return marker;
  }

  getMarkerStyle(gallons) {
    if (gallons > 500) {
      return { color: '#10b981', scale: 12 }; // Green, large
    } else if (gallons > 100) {
      return { color: '#f59e0b', scale: 8 };  // Yellow, medium
    } else {
      return { color: '#ef4444', scale: 6 };  // Red, small
    }
  }

  showInfoWindow(marker, location) {
    const content = this.createInfoWindowContent(location);
    this.infoWindow.setContent(content);
    this.infoWindow.open(this.map, marker);
  }

  createInfoWindowContent(location) {
    const chainNames = location.chain_names.join(', ') || 'Unknown';

    return `
      <div class="p-3 max-w-sm">
        <h3 class="font-semibold text-lg text-gray-900 mb-2">${location.city}, ${location.state}</h3>
        
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600">Transactions:</span>
            <span class="font-medium">${location.transaction_count}</span>
          </div>
          
          <div class="flex justify-between">
            <span class="text-gray-600">Total Gallons:</span>
            <span class="font-medium">${location.total_gallons.toLocaleString()}</span>
          </div>
          
          <div class="flex justify-between">
            <span class="text-gray-600">Total Amount:</span>
            <span class="font-medium">$${location.total_amount.toLocaleString()}</span>
          </div>
          
          <div class="flex justify-between">
            <span class="text-gray-600">Total Savings:</span>
            <span class="font-medium text-green-600">$${location.total_savings.toLocaleString()}</span>
          </div>
          
          <div class="flex justify-between">
            <span class="text-gray-600">Avg Savings/Gal:</span>
            <span class="font-medium text-green-600">$${location.avg_savings_per_gallon}</span>
          </div>
          
          <div class="pt-2 border-t border-gray-200">
            <span class="text-gray-600 text-xs">Chains: ${chainNames}</span>
          </div>
        </div>
      </div>
    `;
  }

  showTransactionDetails(locationKey) {
    // This could open a modal or navigate to a detailed view
    console.log('Show transaction details for:', locationKey);
    // For now, just close the info window
    this.infoWindow.close();
  }
}
